import { lazy, Suspense, type FC } from 'react';
import { LoginInfoMessageEnum } from 'bc-model';
import qs from 'qs';
import { Navigate, Route, Routes as RouterRoutes, type Location } from 'react-router-dom';
import type { AuthAccountWithPassType } from 'api-client/accounts';
import DodConsent from './Login/DodConsent';
import VerifyEmail from './Login/VerifyEmail';
import UserVerificationStatus from './Login/UserVerificationStatus';
import Expired from './Login/Expired';
import LoggedOut from './Login/LoggedOut';
import Reset from './Login/Reset';
import Privacy from 'components/Privacy';
import Terms from 'components/Terms';
import Login from 'components/Login/Login';
import Logout from 'components/Auth/Logout';
import { ODSSpinner } from 'bc-ods';
import { paths } from 'paths';

const AppRoutes = lazy(() => import('./AppRoutes'));

type RouteProps = {
  location: Location;
  user: AuthAccountWithPassType | null;
  loadUser: () => void;
};

const Routes: FC<RouteProps> = ({ location, user, loadUser }) => {
  const loginSearch = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { reason: _loginReason, ...loginRestSearch } = loginSearch;
  const loginNextSearchStr = qs.stringify(loginRestSearch);
  const loginSignedOut = !user && loginSearch.reason === LoginInfoMessageEnum.SIGNED_OUT;

  return (
    <RouterRoutes>
      <Route path={paths.loggedOut} element={<LoggedOut />} />
      <Route path={paths.verifyEmail} element={<VerifyEmail />} />
      <Route path={paths.accountStatus} element={<UserVerificationStatus />} />
      <Route
        path={paths.login}
        element={
          loginSignedOut ? (
            <Navigate to={`${paths.loggedOut}?${loginNextSearchStr}`} replace />
          ) : user ? (
            <Navigate to={(loginSearch.next as string) ?? paths.home} replace />
          ) : (
            <Login />
          )
        }
      />
      <Route
        path={paths.loginExpired}
        element={user ? <Expired /> : <Navigate to={paths.home} replace />}
      />
      <Route
        path={paths.reset}
        element={!user ? <Reset /> : <Navigate to={paths.home} replace />}
      />
      <Route path={paths.logout} element={<Logout />} />
      <Route path={paths.signup} element={<Login />} />
      <Route path={paths.terms} element={<Terms />} />
      <Route path={paths.privacy} element={<Privacy />} />
      <Route
        path={paths.consent}
        element={<DodConsent loadUser={loadUser} />}
      />
      <Route
        path="*"
        element={
          <Suspense
            fallback={
              <div className="d-flex justify-content-center align-items-center h-100">
                <ODSSpinner />
              </div>
            }
          >
            <AppRoutes user={user} location={location} />
          </Suspense>
        }
      />
    </RouterRoutes>
  );
};

export default Routes;
